import React, { FC, useState } from "react";
import { EyeIcon, EyeSlashIcon } from "@heroicons/react/24/solid";
import { Helmet } from "react-helmet-async";
import Input from "shared/Input/Input";
import { Link, useNavigate } from "react-router-dom";
import ButtonPrimary from "shared/Button/ButtonPrimary";
import api from '../../api';
import { connect, useDispatch } from "react-redux";
import { logout, updateUser } from "../../redux/reducers/userReducer";
import { updateFormData } from "../../redux/reducers/formReducer";

export interface PageLoginProps {
    className?: string;
    user?: Object;
}

const PageLogin: FC<PageLoginProps> = ({ className = "", user }) => {
    const dispatch = useDispatch();
    const navigate = useNavigate();
    const [error, setError] = useState<string>('');
    const [loadingButton, setLoadingButton] = useState<boolean>(false);
    const [showPassword, setShowPassword] = useState<boolean>(false);

    const initialValues = {
        "username": "",
        "password": "",
        "grant_type": "password",
        "client_id": process.env.REACT_APP_CLIENT_ID,
        "client_secret": process.env.REACT_APP_CLIENT_SECRET,
        "scope": ""
    }
    const [inputValues, setInputValues] = useState(initialValues);
    const handleInputChange = (e) => {
        setError('');
        setInputValues((prevState) => ({
            ...prevState,
            [e.target.name]: e.target.value,
        }));
    };

    const handleLoginSubmit = (event) => {
        event.preventDefault();

        setLoadingButton(true);
      
        if (inputValues.username.trim() === '' || inputValues.password.trim() === '') {
          setError('Email or Password is required.');
          setLoadingButton(false);
          return;
        }
      
        if (!inputValues.username.match(/\S+@[a-zA-Z0-9.-]+.[a-zA-Z]{2,}$/)) {
          setError('Invalid Email address');
          setLoadingButton(false);
          return;
        }
      
        api.auth(inputValues, (res) => {
            if (res && res.status === 200 && res.data.access_token) {
                localStorage.setItem('access_token', res.data.access_token);

                let lastPage = localStorage.getItem('last_page');
                if(lastPage){
                    window.location.href = lastPage;
                }else{
                    window.location.href = '/';
                }                

            } else {
                setLoadingButton(false);
                if (res.status === 400) {
                    setError(res.data.message);
                } else if (res.status === 403) {
                    setError(res.data.error);
                } else if (res.status === 401) {
                    setError(res.data.error);
                } else {
                    setError(res.data.error || 'An error occurred during login');
                }
            }
        });
    };
      
    return (
        <div className={`nc-PageLogin ${className}`} data-nc-id="PageLogin">
            <div className="flex flex-col md:flex-row w-full min-h-screen">
                <div className="w-full md:w-1/2 p-6 md:p-12 flex flex-col justify-start bg-white dark:bg-gray-900">
                    <div className="max-w-2xl mx-auto pt-12 md:pt-16 animate-in fade-in slide-in-from-left-4 duration-700">
                        <div className="space-y-8">
                            <div>
                                <h1 className="text-4xl md:text-5xl font-bold tracking-tight text-gray-900 dark:text-white">
                                    Welcome to AgentMatch
                                </h1>
                                <h2 className="text-xl md:text-2xl mt-4 text-gray-600 dark:text-gray-300">
                                    Where Fashion Brands and Agents Connect
                                </h2>
                            </div>

                            <div className="text-base md:text-lg space-y-4 text-gray-600 dark:text-gray-300 text-justify">
                                <p>
                                    AgentMatch is the go-to platform for fashion professionals ready to grow. We
                                    connect <strong>ambitious brands</strong> looking to expand their reach with <strong>experienced agents</strong> searching for fresh, exciting labels to represent.
                                </p>

                                <p>
                                    Whether you're a brand eager to break into new markets or an agent looking to
                                    enrich your portfolio, AgentMatch brings the right people together. Our curated
                                    network helps you build strong, strategic partnerships—because when the right
                                    match is made, business takes off.
                                </p>

                                <p>
                                    <strong>Join AgentMatch today and step into a world of new opportunities in fashion.</strong>
                                </p>
                            </div>
                        </div>
                    </div>
                </div>

                <div className="w-full md:w-1/2 p-6 md:p-12 bg-white dark:bg-gray-900 flex flex-col justify-start">
                    <div className="max-w-md mx-auto w-full pt-12 md:pt-16 space-y-8 animate-in fade-in slide-in-from-right-4 duration-700">
                        <div className="text-center">
                            <h3 className="text-2xl md:text-3xl font-bold text-gray-900 dark:text-white">
                                AgentMatch
                            </h3>
                            <p className="text-gray-600 dark:text-gray-400 mt-2">
                                Sign in to your account
                            </p>
                        </div>

                        <div className="flex bg-gray-100 dark:bg-gray-800 rounded-lg p-1">
                            <button className="flex-1 py-2 px-4 text-center text-sm font-medium text-white bg-gray-900 dark:bg-white dark:text-black rounded-md transition-all duration-200">
                                Login
                            </button>
                            <Link
                                to="/register"
                                className="flex-1 py-2 px-4 text-center text-sm font-medium text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-white rounded-md transition-all duration-200"
                            >
                                Register
                            </Link>
                        </div>

                        {/* Error Message */}
                        {error && (
                            <div className="p-4 mb-4 text-sm text-red-700 bg-red-100 rounded-lg dark:bg-red-200 dark:text-red-800" role="alert">
                                {error}
                            </div>
                        )}

                        {/* FORM */}
                        <form onSubmit={handleLoginSubmit} className="space-y-6">
                            <div className="space-y-6">
                                <div>
                                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                                        Email
                                    </label>
                                    <Input
                                        type="email"
                                        name='username'
                                        onChange={handleInputChange}
                                        placeholder="you@example.com"
                                        className="w-full px-3 py-2 border border-gray-300 dark:border-gray-700 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-black dark:focus:ring-white focus:border-transparent dark:bg-gray-800 dark:text-white"
                                        tabIndex={1}
                                    />
                                </div>

                                <div>
                                    <div className="flex justify-between items-center mb-2">
                                        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">
                                            Password
                                        </label>
                                        <Link
                                            to="/forgot-pass"
                                            className="text-sm text-black hover:text-gray-700 dark:text-white dark:hover:text-gray-300 transition-colors"
                                        >
                                            Forgot password?
                                        </Link>
                                    </div>
                                    <div className="relative">
                                        <Input
                                            type={showPassword ? "text" : "password"}
                                            name='password'
                                            onChange={handleInputChange}
                                            className="w-full px-3 py-2 pr-10 border border-gray-300 dark:border-gray-700 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-black dark:focus:ring-white focus:border-transparent dark:bg-gray-800 dark:text-white"
                                            tabIndex={2}
                                        />
                                        <button
                                            type="button"
                                            className="absolute right-3 top-1/2 transform -translate-y-1/2 focus:outline-none text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-200 transition-colors"
                                            onClick={() => setShowPassword(!showPassword)}
                                        >
                                            {showPassword ? (
                                                <EyeSlashIcon className="h-5 w-5" />
                                            ) : (
                                                <EyeIcon className="h-5 w-5" />
                                            )}
                                        </button>
                                    </div>
                                </div>



                                <ButtonPrimary
                                    type="submit"
                                    disabled={loadingButton}
                                    className="w-full bg-gray-900 hover:bg-gray-800 dark:bg-white dark:hover:bg-gray-100 dark:text-gray-900 text-white font-medium py-3 px-4 rounded-md transition-all duration-200 disabled:opacity-50 disabled:cursor-not-allowed"
                                >
                                    {loadingButton ? (
                                        <div className="flex items-center justify-center space-x-2">
                                            <div className="w-5 h-5 border-2 border-white dark:border-gray-900 border-t-transparent rounded-full animate-spin"></div>
                                            <span>Signing in...</span>
                                        </div>
                                    ) : (
                                        "Login"
                                    )}
                                </ButtonPrimary>
                            </div>
                        </form>

                        {/* Terms */}
                        <div className="text-center">
                            <p className="text-xs text-gray-500 dark:text-gray-400">
                                By using AgentMatch, you agree to our{" "}
                                <Link to="/" className="text-black hover:text-gray-700 dark:text-white dark:hover:text-gray-300">
                                    Terms of Service
                                </Link>{" "}
                                and{" "}
                                <Link to="/" className="text-black hover:text-gray-700 dark:text-white dark:hover:text-gray-300">
                                    Privacy Policy
                                </Link>
                                .
                            </p>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
};

const mapStateToProps = (state) => ({
    user: state.user.user,
});

export default connect(mapStateToProps, { updateUser, logout })(
    PageLogin
);